'use client';

import { getHomePageData } from '@/api/home';
import { getSearchDataByType } from '@/api/public';
import { useSearchStore } from './store/search-store';

interface HotKeywordConfig {
  name: string;
  [key: string]: unknown;
}

interface SearchConfig {
  hotKeywords?: HotKeywordConfig[];
  [key: string]: unknown;
}

let configRequest: Promise<void> | null = null;
let productsRequest: Promise<void> | null = null;

/** 首页只预加载配置；聚焦时复用同一请求，配置完成前不加载商品。 */
export const loadSearchConfig = (): Promise<void> => {
  if (configRequest) return configRequest;
  if (useSearchStore.getState().searchData) return Promise.resolve();

  configRequest = Promise.resolve()
    .then(async () => {
      const response = await getHomePageData({ block_type: 9, status: 1 });
      if (response.code !== 200 || !Array.isArray(response.data?.list)) {
        throw new Error('Invalid search configuration response');
      }

      const config: SearchConfig = response.data.list[0] ?? { hotKeywords: [] };
      const keywords: HotKeywordConfig[] = config.hotKeywords ?? [];
      if (!Array.isArray(keywords)) throw new Error('Invalid hot keywords configuration');

      const store = useSearchStore.getState();
      store.setSearchData(config);
      store.setHotProductList(
        keywords.map((item) => ({ ...item, keywords: item.name, products: [] })),
      );
    })
    .finally(() => {
      configRequest = null;
    });

  return configRequest;
};

/** 仅在打开搜索时调用；成功后复用数据，失败释放请求以允许下次重试。 */
export const loadHotSearchProducts = (): Promise<void> => {
  if (productsRequest) return productsRequest;
  if (useSearchStore.getState().isSearchDataLoaded) return Promise.resolve();

  useSearchStore.getState().setIsSearchDataLoading(true);
  productsRequest = Promise.resolve()
    .then(async () => {
      await loadSearchConfig();
      const store = useSearchStore.getState();
      const keywords: HotKeywordConfig[] = store.searchData.hotKeywords ?? [];

      if (keywords.length) {
        const response = await getSearchDataByType('hot', {
          keywords: keywords.map((item) => item.name),
          limit: 6,
        });
        if (response.code !== 200 || !Array.isArray(response.data)) {
          throw new Error('Invalid hot search products response');
        }

        store.setHotProductList(
          keywords.map((item) => {
            const productData = response.data.find((entry) => entry.keywords === item.name);
            return {
              ...item,
              keywords: item.name,
              ...productData,
              products: productData?.products ?? [],
            };
          }),
        );
      }

      // 正常返回空商品或无热搜词也属于成功；请求异常不会执行到这里。
      store.setIsSearchDataLoaded(true);
    })
    .finally(() => {
      useSearchStore.getState().setIsSearchDataLoading(false);
      productsRequest = null;
    });

  return productsRequest;
};
